// @ts-strict-ignore
import {Component, Injector, viewChild} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Assessment, AssessmentItem, FetchDataType} from "@models";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Translations} from "@translations";
import {MatStepper} from "@angular/material/stepper";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {filter} from "rxjs/operators";
import {AssessmentValidator, AssessmentValidatorError} from "./import-from-catalogue-dialog.validator";

export interface DialogData_ImportFromCatalogueDialogInput extends MatDialogDataBaseInput {
	inputCompetenceCategories: string[];
}

export interface DialogData_ImportFromCatalogueDialogOutput extends MatDialogDataBaseOutput {
	exportedAssessmentItems: string[];
	catalogueAssessmentId: string;
}

type ItemsToExportMap = Map<string, InnerMap>;
type InnerMap = {groupName: string; itemMap: Map<string, {checked: boolean; item: AssessmentItem}>};

@UntilDestroy()
@Component({
	selector: "sim-import-from-catalogue-dialog",
	templateUrl: "./import-from-catalogue-dialog.component.html",
	styleUrl: "./import-from-catalogue-dialog.component.scss",
	standalone: false,
})
export class ImportFromCatalogueDialogComponent extends BasicMaterialDialog<
	ImportFromCatalogueDialogComponent,
	DialogData_ImportFromCatalogueDialogInput,
	DialogData_ImportFromCatalogueDialogOutput
> {
	// Enums
	public T = Translations;
	public readonly AssessmentValidatorError = AssessmentValidatorError;

	// Properties
	public chooseAssessmentStepControl = new FormGroup({
		assessmentControl: new FormControl<Assessment>(null, {
			validators: [Validators.required, AssessmentValidator(new Set(this.data.inputCompetenceCategories))],
		}),
	});
	public itemsToExportMap: ItemsToExportMap = new Map<
		string,
		{
			groupName: "";
			itemMap: Map<string, {checked: boolean; item: AssessmentItem}>;
		}
	>();
	public stepper = viewChild.required(MatStepper);

	constructor(_injector: Injector) {
		super(_injector);
	}

	public async ngOnInit() {
		super.ngOnInit();

		this.chooseAssessmentStepControl.controls.assessmentControl.valueChanges
			.pipe(untilDestroyed(this), filter(Boolean))
			.subscribe((assessmentOption) => {
				const setOutput = new Set(assessmentOption.competenceCategoryIds);

				this.itemsToExportMap.clear();
				assessmentOption.assessableGroups.forEach((group) => {
					this.itemsToExportMap.set(group.groupId, {
						groupName: group.name,
						itemMap: new Map(group.items.map((item) => [item.assessableItemId, {checked: false, item: item}])),
					});
				});
			});
	}

	public resetForm() {
		this.itemsToExportMap.clear();
		this.stepper().reset();
	}

	public areAllItemsChecked(groupMapEntry: InnerMap): boolean {
		return Array.from(groupMapEntry.itemMap.values()).every((entry) => entry.checked);
	}

	public areAnyItemsChecked(map: ItemsToExportMap): boolean {
		return Array.from(map.values()).some((groupMapEntry) => Array.from(groupMapEntry.itemMap.values()).some((entry) => entry.checked));
	}

	public toggleAllItems(groupMapEntry: InnerMap, checked: boolean) {
		for (const key of groupMapEntry.itemMap.keys()) {
			groupMapEntry.itemMap.set(key, {checked: checked, item: groupMapEntry.itemMap.get(key).item});
		}
	}

	public doClose() {
		const catalogueId = this.chooseAssessmentStepControl.controls.assessmentControl.value.id;
		this.close({
			action: "ok",
			exportedAssessmentItems: calculateExportedAssessmentItems(this.itemsToExportMap),
			catalogueAssessmentId: catalogueId,
		});

		function calculateExportedAssessmentItems(map: ItemsToExportMap): string[] {
			return Array.from(map.values()).flatMap((groupMapEntry) =>
				Array.from(groupMapEntry.itemMap.entries())
					.filter(([_, itemEntry]) => itemEntry.checked)
					.map(([_, itemEntry]) => itemEntry.item.assessableItemId),
			);
		}
	}

	public handleItemCheckboxClick(groupMapEntry: InnerMap, itemId: string, checked: boolean) {
		groupMapEntry.itemMap.set(itemId, {...groupMapEntry.itemMap.get(itemId), checked: checked});
	}

	protected readonly FetchDataType = FetchDataType;
}
